import { Button } from '@newfront-insurance/core-ui/v2';
import { useMemo, useState } from 'react';

import { GroupAddPeopleDialog } from './group-add-people-dialog';
import { useAdminActions } from './use-admin-actions';
import { useSettingsAdmins } from './use-settings-admins';
import type { SettingsGroup } from '../../../../queries/skill-administration';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { SettingsConfirmationDialog } from '../settings-confirmation-dialog';
import { SettingsPersonRow } from '../settings-person-row';
import { SettingsSectionList } from '../settings-section-list';

/** Each organization owns its reads, dialogs, and pending mutations. */
export function OrganizationAdmins({ organization }: { organization: SettingsGroup }) {
  const { settingsAdministration, userUuid: currentUserUuid } = useChatOverlayContext();
  const currentOrganization = settingsAdministration.organizations.find((root) => root.uuid === organization.uuid);
  const canManageAdmins = !!currentOrganization?.managers.includes(currentUserUuid);
  const [addingTo, setAddingTo] = useState<{ organization: SettingsGroup; userUuid: string } | null>(null);
  const [revoking, setRevoking] = useState<{
    groupUuid: string;
    userUuid: string;
    name: string;
    organization: string;
    actorUuid: string;
  } | null>(null);
  const activeRevocation =
    revoking?.actorUuid === currentUserUuid && canManageAdmins && currentOrganization?.uuid === revoking.groupUuid
      ? revoking
      : null;
  const roots = useMemo(() => (currentOrganization ? [currentOrganization] : []), [currentOrganization]);
  const result = useSettingsAdmins(roots);
  const { admins } = result;
  const loading = settingsAdministration.status === 'loading' || result.loading;
  const accessError = settingsAdministration.status === 'error';
  const error = accessError || result.error;
  const retry = accessError ? settingsAdministration.retry : result.retry;
  const removal = useAdminActions(organization.uuid, result);
  const excludedUsers = useMemo(
    () =>
      new Set([
        currentUserUuid,
        ...admins.filter((admin) => admin.groupUuid === currentOrganization?.uuid).map((admin) => admin.userUuid),
      ]),
    [admins, currentOrganization?.uuid, currentUserUuid],
  );

  return (
    <section aria-label={`Admins in ${organization.name}`} className="space-y-3">
      {removal.error && !activeRevocation ? (
        <p role="alert" className="text-fire-500 text-sm">
          {removal.error}
        </p>
      ) : null}
      {error ? (
        <div role="alert" className="border-steel-75 text-steel-400 rounded-xs border bg-white p-5 text-sm">
          Couldn't load admins for {organization.name}.{' '}
          <Button type="button" variant="inline" className="text-sm" onClick={retry}>
            Retry
          </Button>
        </div>
      ) : (
        <SettingsSectionList
          title={organization.name}
          loading={loading}
          emptyMessage={!loading && !admins.length ? 'No admins yet.' : undefined}
          headerEnd={
            canManageAdmins ? (
              <Button
                type="button"
                variant="tertiary"
                className="h-auto p-0 text-sm leading-4"
                disabled={loading || !!error || !currentOrganization || !currentUserUuid || Boolean(removal.pendingKey)}
                onClick={() => {
                  if (currentOrganization && currentUserUuid)
                    setAddingTo({ organization: currentOrganization, userUuid: currentUserUuid });
                }}
              >
                + Add admin
              </Button>
            ) : undefined
          }
        >
          {loading ? (
            <SettingsPersonRow loadingLabel="Loading admins" />
          ) : admins.length ? (
            admins.map(({ groupUuid, organization: organizationName, userUuid, employee }) => {
              const name = employee?.fullName || employee?.email || 'Unknown employee';
              return (
                <SettingsPersonRow
                  key={`${groupUuid}:${userUuid}`}
                  name={name}
                  description={organizationName}
                  imageUrl={employee?.imageUrl ?? undefined}
                  initials={employee ? undefined : '?'}
                  action={
                    canManageAdmins ? (
                      <Button
                        type="button"
                        variant="transparent"
                        className="text-wtw-coralreef-500 h-auto p-0 text-sm leading-4"
                        aria-label={`Revoke ${name} from ${organizationName}`}
                        disabled={!currentUserUuid || Boolean(removal.pendingKey)}
                        aria-busy={removal.pendingKey === userUuid}
                        onClick={() => {
                          if (!currentUserUuid) return;
                          removal.clearError();
                          setRevoking({
                            groupUuid,
                            userUuid,
                            name,
                            organization: organizationName,
                            actorUuid: currentUserUuid,
                          });
                        }}
                      >
                        {removal.pendingKey === userUuid ? 'Revoking…' : 'Revoke'}
                      </Button>
                    ) : undefined
                  }
                />
              );
            })
          ) : null}
        </SettingsSectionList>
      )}
      {activeRevocation ? (
        <SettingsConfirmationDialog
          title="Revoke admin access"
          description={
            <>
              Revoke {activeRevocation.name}'s admin access to {activeRevocation.organization}? They will remain a
              member of the organization.
            </>
          }
          confirmLabel="Revoke admin access"
          pendingLabel="Revoking…"
          pending={Boolean(removal.pendingKey)}
          disabled={loading || Boolean(error) || settingsAdministration.status !== 'ready'}
          error={removal.error}
          onClose={() => setRevoking(null)}
          onConfirm={() =>
            void removal
              .revokeAdmin(activeRevocation.groupUuid, activeRevocation.userUuid, activeRevocation.name)
              ?.then((succeeded) => {
                if (succeeded) setRevoking(null);
              })
          }
        />
      ) : null}
      {canManageAdmins &&
      currentOrganization &&
      addingTo?.organization.uuid === currentOrganization.uuid &&
      addingTo.userUuid === currentUserUuid ? (
        <GroupAddPeopleDialog
          key={`${addingTo.userUuid}:${addingTo.organization.uuid}`}
          kind="admins"
          groupId={addingTo.organization.uuid}
          parentGroupUuid={null}
          excludedUserUuids={excludedUsers}
          onClose={() => setAddingTo(null)}
          onChanged={removal.refreshAdmins}
        />
      ) : null}
    </section>
  );
}
